import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  AutoAwesomeOutlined,
  CloudDownloadOutlined,
  KeyboardArrowDownRounded,
  PlayArrowRounded,
  ScienceOutlined,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import axiosInstance from "../axiosInstance";
import TenantAutocomplete from "./TenantAutocomplete";

const DEFAULT_AUTOMATION = {
  enabled: false,
  frequencyMonths: 3,
  overwriteFaqs: false,
  scheduleAnchorAt: null,
  nextRunAt: null,
  activeRun: null,
};

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    minHeight: 56,
    borderRadius: "16px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
    "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
    "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
  },
  "& label.Mui-focused": { color: "#f97316" },
};

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const candidates = [
    typeof data === "string" ? data : null,
    data?.message,
    typeof data?.error === "string" ? data.error : data?.error?.message,
    data?.title,
  ];
  const apiMessage = candidates.find(
    (message) => typeof message === "string" && message.trim()
  );
  return apiMessage?.trim() || fallback || "Something went wrong. Please try again.";
};

const isActiveStatus = (status) => ["queued", "running"].includes(String(status).toLowerCase());
const getRunId = (run) =>
  run?.id ||
  run?.runId ||
  run?.Id ||
  run?.RunId ||
  run?.run?.id ||
  run?.run?.runId ||
  null;

const normalizeRun = (run, fallbackId = null) => {
  if (!run || typeof run !== "object" || Array.isArray(run)) return null;
  const status = run.status || run.Status;
  if (!status) return null;
  const trigger = run.trigger || run.Trigger;
  const backendRunType = run.runType || run.RunType;
  const isShopifyPublication = String(backendRunType).toLowerCase() === "shopifypublication";
  const publishedProducts = run.publishedProducts ?? run.PublishedProducts ?? 0;
  const skippedProducts = run.skippedProducts ?? run.SkippedProducts ?? 0;
  const failedProducts = run.failedProducts ?? run.FailedProducts ?? 0;
  return {
    ...run,
    id: getRunId(run) || fallbackId,
    status,
    trigger,
    runType: isShopifyPublication ? "shopify" : backendRunType,
    phase: run.phase || run.Phase || "None",
    processedProducts: isShopifyPublication
      ? publishedProducts + skippedProducts + failedProducts
      : run.processedProducts ?? run.ProcessedProducts ?? 0,
    totalProducts: run.totalProducts ?? run.TotalProducts ?? 0,
    publishedProducts,
    skippedProducts,
    failedProducts,
    queryStringFilter: run.queryStringFilter ?? run.QueryStringFilter ?? null,
    onlyNewProducts: run.onlyNewProducts ?? run.OnlyNewProducts ?? false,
    errorSummary: run.errorSummary ?? run.ErrorSummary ?? null,
  };
};

const normalizeShopifyRun = (run, fallbackId = null) => {
  if (!run || typeof run !== "object" || Array.isArray(run)) return null;
  const runId = getRunId(run) || fallbackId;
  const status = run.status || run.Status;
  if (!runId || !status) return null;
  const publishedProducts = run.publishedProducts ?? run.PublishedProducts ?? 0;
  const skippedProducts = run.skippedProducts ?? run.SkippedProducts ?? 0;
  const failedProducts = run.failedProducts ?? run.FailedProducts ?? 0;
  return {
    ...run,
    id: runId,
    runId,
    status,
    runType: "shopify",
    totalProducts: run.totalProducts ?? run.TotalProducts ?? 0,
    publishedProducts,
    skippedProducts,
    failedProducts,
    processedProducts: publishedProducts + skippedProducts + failedProducts,
    errorSummary: run.errorSummary ?? run.ErrorSummary ?? null,
  };
};

const getRunIdFromLocation = (location) => {
  if (!location) return null;
  const match = String(location).match(/\/runs\/([^/?#]+)(?:[/?#]|$)/i);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const getRunTimestamp = (run) => {
  const timestamp = new Date(run?.startedAt || run?.completedAt || 0).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const statusColor = (status) => {
  switch (String(status).toLowerCase()) {
    case "succeeded": return "success";
    case "failed": return "error";
    case "running": return "info";
    default: return "warning";
  }
};

const FaqHighlightsTab = ({ tenants = [], selectedTenant, setSelectedTenant }) => {
  const [tenantId, setTenantId] = useState(selectedTenant || "");
  const [activeView, setActiveView] = useState(0);
  const [automation, setAutomation] = useState(DEFAULT_AUTOMATION);
  const [frequency, setFrequency] = useState(3);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const [savingFrequency, setSavingFrequency] = useState(false);
  const [savingOverwriteFaqs, setSavingOverwriteFaqs] = useState(false);
  const [startingRun, setStartingRun] = useState(false);
  const [exportingMetafields, setExportingMetafields] = useState(false);
  const [shopifyRun, setShopifyRun] = useState(null);
  const [queryStringFilter, setQueryStringFilter] = useState("");
  const [onlyNewProducts, setOnlyNewProducts] = useState(false);
  const [currentRun, setCurrentRun] = useState(null);
  const [productId, setProductId] = useState("");
  const [previewOverwriteFaqs, setPreviewOverwriteFaqs] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [productIdError, setProductIdError] = useState("");
  const [notice, setNotice] = useState(null);
  const pollTimerRef = useRef(null);
  const pollingRunIdRef = useRef(null);
  const shopifyPollTimerRef = useRef(null);
  const shopifyPollingRunIdRef = useRef(null);

  const currentTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === tenantId) || null,
    [tenantId, tenants]
  );
  const notifyParent = typeof setSelectedTenant === "function" ? setSelectedTenant : () => {};

  useEffect(() => {
    if (selectedTenant !== undefined && selectedTenant !== tenantId) {
      setTenantId(selectedTenant || "");
    }
  }, [selectedTenant, tenantId]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollingRunIdRef.current = null;
  }, []);

  const stopShopifyPolling = useCallback(() => {
    if (shopifyPollTimerRef.current) {
      clearTimeout(shopifyPollTimerRef.current);
      shopifyPollTimerRef.current = null;
    }
    shopifyPollingRunIdRef.current = null;
  }, []);

  const loadTenantData = useCallback(async (id, { showLoading = true } = {}) => {
    if (!id) return;
    if (showLoading) setLoading(true);
    try {
      const [stateResponse, runsResponse] = await Promise.all([
        axiosInstance.get(`/faq-highlights-automation/${encodeURIComponent(id)}`),
        axiosInstance.get(`/faq-highlights-automation/${encodeURIComponent(id)}/runs`),
      ]);
      const nextAutomation = { ...DEFAULT_AUTOMATION, ...(stateResponse.data || {}) };
      const nextRuns = Array.isArray(runsResponse.data) ? runsResponse.data : [];
      const normalizedRuns = nextRuns.map((run) => normalizeRun(run)).filter(Boolean);
      const activeRun =
        normalizeRun(nextAutomation.activeRun) ||
        normalizedRuns.find((run) => isActiveStatus(run.status)) ||
        null;
      nextAutomation.activeRun = activeRun;
      setAutomation(nextAutomation);
      setFrequency(nextAutomation.frequencyMonths);
      setPreviewOverwriteFaqs(Boolean(nextAutomation.overwriteFaqs));
      setRuns([...normalizedRuns].sort((a, b) => getRunTimestamp(b) - getRunTimestamp(a)));
      setCurrentRun(activeRun);
      return activeRun;
    } catch (error) {
      setNotice({ severity: "error", message: getErrorMessage(error, "Failed to load FAQ automation.") });
      return null;
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    stopPolling();
    stopShopifyPolling();
    setAutomation(DEFAULT_AUTOMATION);
    setFrequency(3);
    setPreviewOverwriteFaqs(false);
    setRuns([]);
    setCurrentRun(null);
    setShopifyRun(null);
    setQueryStringFilter("");
    setOnlyNewProducts(false);
    setPreview(null);
    setProductIdError("");
    if (tenantId) loadTenantData(tenantId);
    return () => {
      stopPolling();
      stopShopifyPolling();
    };
  }, [tenantId, loadTenantData, stopPolling, stopShopifyPolling]);

  const refreshAfterRun = useCallback(async () => {
    if (!tenantId) return;
    await loadTenantData(tenantId, { showLoading: false });
  }, [tenantId, loadTenantData]);

  const pollRun = useCallback(async (runId) => {
    if (!tenantId || !runId) return;
    try {
      const { data } = await axiosInstance.get(
        `/faq-highlights-automation/${encodeURIComponent(tenantId)}/runs/${encodeURIComponent(runId)}`
      );
      if (pollingRunIdRef.current !== runId) return;
      const nextRun = normalizeRun(data, runId);
      if (!nextRun) {
        stopPolling();
        setCurrentRun(null);
        setNotice({
          severity: "error",
          message: "The run progress response was invalid. Polling has stopped.",
        });
        return;
      }
      setCurrentRun(nextRun);
      if (isActiveStatus(nextRun.status)) {
        pollTimerRef.current = setTimeout(() => pollRun(runId), 5000);
      } else {
        stopPolling();
        await refreshAfterRun();
        if (String(nextRun.status).toLowerCase() === "failed") {
          setNotice({ severity: "error", message: nextRun.errorSummary || "The full import failed." });
        } else {
          setNotice({ severity: "success", message: "Full import completed." });
        }
      }
    } catch (error) {
      if (pollingRunIdRef.current !== runId) return;
      stopPolling();
      setNotice({ severity: "error", message: getErrorMessage(error, "Failed to retrieve run progress.") });
      await refreshAfterRun();
    }
  }, [refreshAfterRun, stopPolling, tenantId]);

  const startPolling = useCallback((runId) => {
    if (!runId || pollingRunIdRef.current === runId) return;
    stopPolling();
    pollingRunIdRef.current = runId;
    pollRun(runId);
  }, [pollRun, stopPolling]);

  useEffect(() => {
    const runId = getRunId(currentRun);
    if (!runId || !isActiveStatus(currentRun.status)) {
      stopPolling();
      return undefined;
    }
    startPolling(runId);
    return undefined;
  }, [currentRun, startPolling, stopPolling]);

  const pollShopifyRun = useCallback(async (runId) => {
    if (!tenantId || !runId) return;
    try {
      const { data } = await axiosInstance.get(
        `/faq-highlights-automation/${encodeURIComponent(tenantId)}/shopify/runs/${encodeURIComponent(runId)}`
      );
      if (shopifyPollingRunIdRef.current !== runId) return;
      const nextRun = normalizeShopifyRun(data, runId);
      if (!nextRun) {
        stopShopifyPolling();
        setNotice({ severity: "error", message: "The Shopify export progress response was invalid." });
        return;
      }
      setShopifyRun(nextRun);
      if (isActiveStatus(nextRun.status)) {
        shopifyPollTimerRef.current = setTimeout(() => pollShopifyRun(runId), 5000);
      } else {
        stopShopifyPolling();
        if (String(nextRun.status).toLowerCase() === "failed") {
          setNotice({ severity: "error", message: nextRun.errorSummary || "The Shopify metafield export failed." });
        } else {
          setNotice({ severity: "success", message: "Shopify metafield export completed." });
        }
      }
    } catch (error) {
      if (shopifyPollingRunIdRef.current !== runId) return;
      stopShopifyPolling();
      setNotice({ severity: "error", message: getErrorMessage(error, "Failed to retrieve Shopify export progress.") });
    }
  }, [stopShopifyPolling, tenantId]);

  const startShopifyPolling = useCallback((runId) => {
    if (!runId || shopifyPollingRunIdRef.current === runId) return;
    stopShopifyPolling();
    shopifyPollingRunIdRef.current = runId;
    pollShopifyRun(runId);
  }, [pollShopifyRun, stopShopifyPolling]);

  const handleTenantChange = (_, tenant) => {
    const id = tenant?.id || "";
    setTenantId(id);
    notifyParent(id);
  };

  const handleEnableChange = async (_, enabled) => {
    if (!tenantId || savingEnabled) return;
    const previousEnabled = automation.enabled;
    setAutomation((previous) => ({ ...previous, enabled }));
    setSavingEnabled(true);
    try {
      await axiosInstance.patch(`/faq-highlights-automation/${encodeURIComponent(tenantId)}/enabled`, { enabled });
    } catch (error) {
      setAutomation((previous) => ({ ...previous, enabled: previousEnabled }));
      setNotice({ severity: "error", message: getErrorMessage(error, "Failed to update automation status.") });
    } finally {
      setSavingEnabled(false);
    }
  };

  const handleSaveFrequency = async () => {
    if (!tenantId || !automation.enabled || savingFrequency || frequency === automation.frequencyMonths) return;
    setSavingFrequency(true);
    try {
      await axiosInstance.put(`/faq-highlights-automation/${encodeURIComponent(tenantId)}/frequency`, {
        frequencyMonths: frequency,
      });
      setAutomation((previous) => ({ ...previous, frequencyMonths: frequency }));
      setNotice({ severity: "success", message: "Frequency saved. A full import starts or resets the automation schedule." });
    } catch (error) {
      setFrequency(automation.frequencyMonths);
      setNotice({ severity: "error", message: getErrorMessage(error, "Failed to save frequency.") });
    } finally {
      setSavingFrequency(false);
    }
  };

  const handleOverwriteFaqsChange = async (_, overwriteFaqs) => {
    if (!tenantId || savingOverwriteFaqs) return;
    const previousOverwriteFaqs = automation.overwriteFaqs;
    setAutomation((previous) => ({ ...previous, overwriteFaqs }));
    setPreviewOverwriteFaqs(overwriteFaqs);
    setSavingOverwriteFaqs(true);
    try {
      await axiosInstance.patch(
        `/faq-highlights-automation/${encodeURIComponent(tenantId)}/overwrite-faqs`,
        { overwriteFaqs }
      );
      setNotice({
        severity: "success",
        message: overwriteFaqs
          ? "Future FAQ imports will replace existing FAQs."
          : "Future FAQ imports will preserve and append to existing FAQs.",
      });
    } catch (error) {
      setAutomation((previous) => ({ ...previous, overwriteFaqs: previousOverwriteFaqs }));
      setPreviewOverwriteFaqs(previousOverwriteFaqs);
      setNotice({ severity: "error", message: getErrorMessage(error, "Failed to update the FAQ import mode.") });
    } finally {
      setSavingOverwriteFaqs(false);
    }
  };

  const handleStartRun = async () => {
    const hasActiveRun = isActiveStatus(currentRun?.status) || isActiveStatus(automation.activeRun?.status);
    if (!tenantId || hasActiveRun || startingRun) return;
    setStartingRun(true);
    try {
      const response = await axiosInstance.post(
        `/faq-highlights-automation/${encodeURIComponent(tenantId)}/runs`,
        {
          queryStringFilter: queryStringFilter.trim() || null,
          onlyNewProducts,
        }
      );
      const locationRunId = getRunIdFromLocation(response.headers?.location);
      let startedRun = normalizeRun(response.data, locationRunId);

      if (!getRunId(startedRun)) {
        startedRun = await loadTenantData(tenantId, { showLoading: false });
      }

      const runId = getRunId(startedRun);
      if (runId) {
        const queuedRun = normalizeRun(startedRun, runId);
        setCurrentRun(queuedRun);
        startPolling(runId);
      }
      setNotice({ severity: "success", message: "Full import queued." });
    } catch (error) {
      // Queueing failures are persisted as failed runs by the backend, so refresh
      // for every error to surface the failed run and release the active-run UI.
      await refreshAfterRun();
      setNotice({ severity: "error", message: getErrorMessage(error, "Unable to start a full import.") });
    } finally {
      setStartingRun(false);
    }
  };

  const handleExportMetafields = async () => {
    if (!tenantId || exportingMetafields || isActiveStatus(shopifyRun?.status)) return;
    setExportingMetafields(true);
    try {
      const { data } = await axiosInstance.post(
        `/faq-highlights-automation/${encodeURIComponent(tenantId)}/shopify/runs`
      );
      const runId = getRunId(data);
      if (!runId) throw new Error("The Shopify export did not return a run ID.");
      setShopifyRun(normalizeShopifyRun({ ...data, status: "Queued" }, runId));
      startShopifyPolling(runId);
      setNotice({ severity: "success", message: "FAQ and highlight export to Shopify metafields started." });
    } catch (error) {
      setNotice({
        severity: "error",
        message: getErrorMessage(error, "Unable to export FAQs and highlights to Shopify metafields."),
      });
    } finally {
      setExportingMetafields(false);
    }
  };

  const handlePreview = async () => {
    const trimmedProductId = productId.trim();
    if (!trimmedProductId) {
      setProductIdError("Enter a product ID to run a preview.");
      return;
    }
    if (!tenantId) return;
    setProductIdError("");
    setPreview(null);
    setPreviewLoading(true);
    try {
      const { data } = await axiosInstance.post(`/faq-highlights-automation/${encodeURIComponent(tenantId)}/preview`, {
        productId: trimmedProductId,
        overwriteFaqs: previewOverwriteFaqs,
      });
      setPreview(data);
    } catch (error) {
      setNotice({ severity: "error", message: getErrorMessage(error, "Failed to generate a product preview.") });
    } finally {
      setPreviewLoading(false);
    }
  };

  const activeRun = isActiveStatus(currentRun?.status) ? currentRun : automation.activeRun;
  const runIsActive = Boolean(activeRun && isActiveStatus(activeRun.status));
  const progress = activeRun?.totalProducts > 0
    ? Math.min(100, Math.round((activeRun.processedProducts / activeRun.totalProducts) * 100))
    : null;
  const phaseLabel = String(activeRun?.status).toLowerCase() === "queued"
    ? null
    : String(activeRun?.phase).toLowerCase() === "highlights"
      ? "Generating Highlights"
      : "Generating FAQs";
  const shopifyRunIsActive = isActiveStatus(shopifyRun?.status);
  const shopifyProgress = shopifyRun?.totalProducts > 0
    ? Math.min(100, Math.round((shopifyRun.processedProducts / shopifyRun.totalProducts) * 100))
    : null;
  const displayedRuns = useMemo(() => {
    const combinedRuns = shopifyRun
      ? [shopifyRun, ...runs.filter((run) => run.id !== shopifyRun.id)]
      : runs;
    return [...combinedRuns].sort((a, b) => getRunTimestamp(b) - getRunTimestamp(a)).slice(0, 20);
  }, [runs, shopifyRun]);
  const frequencyDirty = frequency !== automation.frequencyMonths;

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 1, md: 2 }, py: { xs: 1, md: 2 } }}>
      <Box sx={{ borderRadius: { xs: 4, md: 6 }, background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.28), rgba(255,255,255,0.94) 42%), linear-gradient(180deg, rgba(255, 248, 243, 0.96), rgba(255, 255, 255, 0.92))", boxShadow: "0 24px 60px rgba(236, 123, 55, 0.10)", border: "1px solid rgba(255, 132, 76, 0.10)", p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Box sx={{ width: { xs: 56, md: 64 }, height: { xs: 56, md: 64 }, borderRadius: "20px", background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)", boxShadow: "0 18px 38px rgba(255, 139, 71, 0.30)", display: "grid", placeItems: "center", color: "#fff" }}>
          <AutoAwesomeOutlined sx={{ fontSize: 30 }} />
        </Box>
        <Box>
          <Typography variant="h3" sx={{ fontSize: { xs: "2rem", md: "2.55rem" }, lineHeight: 1.05, fontWeight: 800, color: "#202531", letterSpacing: "-0.03em", mb: 0.8 }}>
            FAQs &amp; Highlights
          </Typography>
          <Typography variant="body1" sx={{ color: "#6b7280", fontWeight: 500 }}>
            Generate and schedule product FAQs and highlights for a customer.
          </Typography>
        </Box>
      </Box>

      <Card elevation={0} sx={{ mb: 3.25, borderRadius: "24px", border: "1px solid rgba(255, 132, 76, 0.10)", backgroundColor: "rgba(255,255,255,0.88)", boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)", overflow: "visible" }}>
        <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
          <Typography variant="caption" sx={{ display: "block", color: "#6b7280", fontWeight: 700, mb: 0.9, px: 0.5 }}>Select Tenant</Typography>
          <TenantAutocomplete tenants={tenants} value={currentTenant} onChange={handleTenantChange} placeholder="Choose a tenant" popupIcon={<KeyboardArrowDownRounded sx={{ color: "#6b7280" }} />} />
        </CardContent>
      </Card>

      <Tabs value={activeView} onChange={(_, value) => setActiveView(value)} sx={{ mb: 2.5, minHeight: 44, "& .MuiTabs-indicator": { backgroundColor: "#f97316", height: 3, borderRadius: "999px" }, "& .MuiTab-root": { minHeight: 44, textTransform: "none", fontWeight: 700, color: "#6b7280", "&.Mui-selected": { color: "#f97316" } } }}>
        <Tab label="Manage Schedule" />
        <Tab label="Test Product" />
      </Tabs>

      {loading ? (
        <Box sx={{ py: 8, display: "grid", placeItems: "center", gap: 1.5 }}><CircularProgress sx={{ color: "#f97316" }} /><Typography color="text.secondary">Loading automation settings…</Typography></Box>
      ) : activeView === 0 ? (
        <Box sx={{ display: "grid", gap: 2 }}>
          <Card elevation={0} sx={{ borderRadius: "22px", border: "1px solid rgba(255, 132, 76, 0.10)", boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)" }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="overline" sx={{ color: "#6b7280", fontWeight: 800, letterSpacing: "0.08em" }}>Schedule settings</Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, py: 2, borderBottom: "1px solid rgba(15, 23, 42, 0.08)" }}>
                <Box><Typography fontWeight={800} color="#202531">Enable</Typography><Typography variant="body2" color="text.secondary">Turn on recurring automation for this customer.</Typography></Box>
                <Switch checked={automation.enabled} onChange={handleEnableChange} disabled={!tenantId || savingEnabled} inputProps={{ "aria-label": "Enable FAQ automation" }} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#16a34a" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#16a34a" } }} />
              </Box>
              <Box sx={{ pt: 2.5, opacity: automation.enabled ? 1 : 0.45 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
                  <Typography fontWeight={600}>Every</Typography>
                  <FormControl size="small" disabled={!tenantId || !automation.enabled} sx={{ minWidth: 140 }}>
                    <InputLabel id="faq-frequency-label">Frequency</InputLabel>
                    <Select labelId="faq-frequency-label" label="Frequency" value={frequency} onChange={(event) => setFrequency(Number(event.target.value))} sx={{ borderRadius: "12px" }}>
                      {[1, 2, 3, 4, 5, 6].map((months) => <MenuItem key={months} value={months}>{months} {months === 1 ? "month" : "months"}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button variant="contained" onClick={handleSaveFrequency} disabled={!tenantId || !automation.enabled || !frequencyDirty || savingFrequency} sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, bgcolor: "#202531", "&:hover": { bgcolor: "#111214" } }}>{savingFrequency ? "Saving…" : "Save frequency"}</Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>A full import starts or resets the automation schedule.</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, pt: 2.5, mt: 2.5, borderTop: "1px solid rgba(15, 23, 42, 0.08)" }}>
                <Box>
                  <Typography fontWeight={800} color="#202531">Overwrite existing FAQs</Typography>
                  <Typography variant="body2" color="text.secondary">Replace each product&apos;s current FAQs during an import. When off, new FAQs are appended up to the configured limit.</Typography>
                </Box>
                <Switch checked={Boolean(automation.overwriteFaqs)} onChange={handleOverwriteFaqsChange} disabled={!tenantId || savingOverwriteFaqs} inputProps={{ "aria-label": "Overwrite existing FAQs during import" }} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#f97316" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#f97316" } }} />
              </Box>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ borderRadius: "22px", border: "1px solid rgba(255, 132, 76, 0.10)", boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)" }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", gap: 1.25 }}>
                  <Chip size="small" label={automation.enabled ? "Enabled" : "Off"} color={automation.enabled ? "success" : "default"} sx={{ mt: 0.2, fontWeight: 800 }} />
                  <Box><Typography fontWeight={800} color="#202531">{automation.enabled ? `Runs every ${automation.frequencyMonths} ${automation.frequencyMonths === 1 ? "month" : "months"}` : "Automation is off"}</Typography>{automation.enabled && <Typography variant="body2" sx={{ color: automation.nextRunAt ? "#6b7280" : "#a16207", mt: 0.25 }}>{automation.nextRunAt ? `Next run: ${formatDateTime(automation.nextRunAt)}` : "No schedule anchor yet — run a full import to start the clock."}</Typography>}</Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <Button variant="contained" startIcon={<PlayArrowRounded />} onClick={handleStartRun} disabled={!tenantId || runIsActive || startingRun} sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, bgcolor: "#202531", "&:hover": { bgcolor: "#111214" } }}>{startingRun ? "Starting…" : "Run full import"}</Button>
                  <Button variant="outlined" startIcon={exportingMetafields ? <CircularProgress size={18} color="inherit" /> : <CloudDownloadOutlined />} onClick={handleExportMetafields} disabled={!tenantId || exportingMetafields || shopifyRunIsActive} sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, color: "#202531", borderColor: "rgba(32, 37, 49, 0.35)", "&:hover": { borderColor: "#202531", bgcolor: "rgba(32, 37, 49, 0.04)" } }}>{exportingMetafields ? "Exporting…" : shopifyRunIsActive ? "Export in progress…" : "Export to Shopify Metafields"}</Button>
                </Box>
              </Box>
              <Box sx={{ mt: 2.5, pt: 2.5, borderTop: "1px solid rgba(15, 23, 42, 0.08)" }}>
                <TextField
                  fullWidth
                  label="Product filter (optional)"
                  placeholder={'e.g. AiMandatoryFields.Brand.keyword:"Nike"'}
                  value={queryStringFilter}
                  onChange={(event) => setQueryStringFilter(event.target.value)}
                  disabled={!tenantId || runIsActive || startingRun}
                  helperText="Uses Elasticsearch query-string syntax. Leave blank to generate content for every product."
                  sx={FIELD_SX}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mt: 2, p: 2, borderRadius: "16px", bgcolor: "rgba(249, 115, 22, 0.06)", border: "1px solid rgba(249, 115, 22, 0.12)" }}>
                  <Box>
                    <Typography fontWeight={800} color="#202531">Only products missing generated content</Typography>
                    <Typography variant="body2" color="text.secondary">Skip FAQ generation when a product already has FAQs, and independently skip highlight generation when it already has highlights.</Typography>
                  </Box>
                  <Switch
                    checked={onlyNewProducts}
                    onChange={(_, checked) => setOnlyNewProducts(checked)}
                    disabled={!tenantId || runIsActive || startingRun}
                    inputProps={{ "aria-label": "Only generate missing FAQ and highlight content" }}
                    sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#f97316" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#f97316" } }}
                  />
                </Box>
              </Box>
              {runIsActive && <Box sx={{ mt: 2.5 }}><LinearProgress variant={progress === null ? "indeterminate" : "determinate"} value={progress ?? 0} sx={{ height: 9, borderRadius: 9, backgroundColor: "#e5e7eb", "& .MuiLinearProgress-bar": { backgroundColor: "#2563eb", borderRadius: 9 } }} /><Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.8, gap: 2 }}>{phaseLabel && <Typography variant="body2" sx={{ color: "#2563eb", fontWeight: 700 }}>{phaseLabel}</Typography>}<Typography variant="body2" sx={{ color: "#2563eb", fontWeight: 700, ml: "auto" }}>{activeRun.totalProducts > 0 ? `${activeRun.processedProducts.toLocaleString()} of ${activeRun.totalProducts.toLocaleString()} products` : "Queued"}</Typography></Box></Box>}
              {shopifyRunIsActive && <Box sx={{ mt: 2.5 }}><LinearProgress variant={shopifyProgress === null ? "indeterminate" : "determinate"} value={shopifyProgress ?? 0} sx={{ height: 9, borderRadius: 9, backgroundColor: "#e5e7eb", "& .MuiLinearProgress-bar": { backgroundColor: "#7c3aed", borderRadius: 9 } }} /><Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.8, gap: 2 }}><Typography variant="body2" sx={{ color: "#7c3aed", fontWeight: 700 }}>Exporting to Shopify metafields</Typography><Typography variant="body2" sx={{ color: "#7c3aed", fontWeight: 700 }}>{shopifyRun.totalProducts > 0 ? `${shopifyRun.processedProducts.toLocaleString()} of ${shopifyRun.totalProducts.toLocaleString()} products` : shopifyRun.status}</Typography></Box></Box>}
              {runIsActive && activeRun.queryStringFilter && <Typography variant="caption" display="block" sx={{ mt: 1, color: "#6b7280" }}>Filter: <Box component="code">{activeRun.queryStringFilter}</Box></Typography>}
              {runIsActive && activeRun.onlyNewProducts && <Typography variant="caption" display="block" sx={{ mt: 0.5, color: "#6b7280" }}>Scope: only missing FAQ and highlight content</Typography>}
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ borderRadius: "22px", border: "1px solid rgba(255, 132, 76, 0.10)", boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)" }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1.5 }}><Typography variant="overline" sx={{ color: "#6b7280", fontWeight: 800, letterSpacing: "0.08em" }}>Run history</Typography><Typography variant="caption" color="text.secondary">Latest 20 runs</Typography></Box>
              {displayedRuns.length === 0 ? <Typography color="text.secondary" sx={{ py: 2 }}>No runs yet.</Typography> : <Box sx={{ overflowX: "auto" }}><Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 900, "& th": { textAlign: "left", color: "#6b7280", fontWeight: 800, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em", py: 1.25, borderBottom: "1px solid rgba(15, 23, 42, 0.08)" }, "& td": { py: 1.4, borderBottom: "1px solid rgba(15, 23, 42, 0.06)", fontSize: "0.9rem" }, "& tr:last-child td": { borderBottom: 0 } }}><thead><tr><th>Date &amp; time</th><th>Type</th><th>Scope</th><th>Filter</th><th>Products processed</th><th>Status</th></tr></thead><tbody>{displayedRuns.map((run) => <tr key={`${run.runType || "content"}-${run.id}`}><td>{formatDateTime(run.startedAt || run.completedAt)}</td><td><Chip size="small" label={run.runType === "shopify" ? "Shopify export" : run.trigger === "Scheduled" ? "Scheduled" : "Full import"} sx={{ fontWeight: 700, backgroundColor: run.runType === "shopify" ? "#f3e8ff" : "#eff6ff", color: run.runType === "shopify" ? "#7e22ce" : "#1d4ed8" }} /></td><td>{run.runType === "shopify" ? "Metafields" : run.onlyNewProducts ? "Missing content" : "All matched"}</td><td><Box component="code" sx={{ display: "block", maxWidth: 240, overflowWrap: "anywhere", color: "#475569" }}>{run.runType === "shopify" ? "—" : run.queryStringFilter || "All products"}</Box></td><td>{run.totalProducts > 0 ? `${(run.processedProducts ?? 0).toLocaleString()} of ${run.totalProducts.toLocaleString()}` : "—"}</td><td><Box><Chip size="small" label={run.status || "Queued"} color={statusColor(run.status)} sx={{ fontWeight: 700 }} />{String(run.status).toLowerCase() === "failed" && run.errorSummary && <Typography variant="caption" display="block" color="error" sx={{ mt: 0.4, maxWidth: 260 }}>{run.errorSummary}</Typography>}</Box></td></tr>)}</tbody></Box></Box>}
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Card elevation={0} sx={{ borderRadius: "22px", border: "1px solid rgba(255, 132, 76, 0.10)", boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)" }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="overline" sx={{ color: "#6b7280", fontWeight: 800, letterSpacing: "0.08em" }}>Test a single product</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>Preview generated FAQs and highlights without affecting the schedule or run history.</Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2, p: 2, borderRadius: "16px", bgcolor: "rgba(249, 115, 22, 0.06)", border: "1px solid rgba(249, 115, 22, 0.12)" }}>
              <Box>
                <Typography fontWeight={800} color="#202531">Preview FAQ overwrite</Typography>
                <Typography variant="body2" color="text.secondary">Show the replacement FAQ set instead of preserving and appending to the current set. This only affects this preview.</Typography>
              </Box>
              <Switch checked={previewOverwriteFaqs} onChange={(_, checked) => setPreviewOverwriteFaqs(checked)} disabled={!tenantId || previewLoading} inputProps={{ "aria-label": "Overwrite existing FAQs in preview" }} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#f97316" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#f97316" } }} />
            </Box>
            <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", flexWrap: { xs: "wrap", sm: "nowrap" } }}><TextField fullWidth label="Product ID" placeholder="e.g. 7486202413190" value={productId} onChange={(event) => { setProductId(event.target.value); setProductIdError(""); }} error={Boolean(productIdError)} helperText={productIdError} disabled={!tenantId || previewLoading} sx={FIELD_SX} /><Button variant="contained" startIcon={<ScienceOutlined />} onClick={handlePreview} disabled={!tenantId || previewLoading} sx={{ minHeight: 56, minWidth: 132, borderRadius: "16px", textTransform: "none", fontWeight: 700, bgcolor: "#202531", "&:hover": { bgcolor: "#111214" } }}>{previewLoading ? "Running…" : "Run test"}</Button></Box>
            {previewLoading && <Box sx={{ mt: 2.5 }}><LinearProgress sx={{ height: 9, borderRadius: 9, backgroundColor: "#e5e7eb", "& .MuiLinearProgress-bar": { backgroundColor: "#2563eb", borderRadius: 9 } }} /><Typography variant="body2" sx={{ color: "#2563eb", fontWeight: 700, mt: 0.8 }}>Generating preview…</Typography></Box>}
            {preview && <Box sx={{ display: "grid", gap: 2.5, mt: 3 }}><JsonSection title="FAQs" value={preview.faqs ?? []} /><JsonSection title="Highlights" value={preview.highlights ?? []} /></Box>}
          </CardContent>
        </Card>
      )}

      </Box>
      <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}><Alert severity={notice?.severity || "info"} onClose={() => setNotice(null)} variant="filled">{notice?.message}</Alert></Snackbar>
    </Box>
  );
};

const JsonSection = ({ title, value }) => (
  <Box>
    <Typography variant="overline" sx={{ color: "#6b7280", fontWeight: 800, letterSpacing: "0.08em" }}>{title} {Array.isArray(value) && <Chip size="small" label={value.length} sx={{ height: 18, ml: 0.5, fontSize: "0.66rem" }} />}</Typography>
    <Box component="pre" sx={{ m: 0, mt: 0.75, p: { xs: 1.5, md: 2 }, overflowX: "auto", borderRadius: "14px", bgcolor: "#1e1e1e", color: "#d1d5db", fontSize: "0.8rem", lineHeight: 1.6 }}>{JSON.stringify(value, null, 2)}</Box>
  </Box>
);

JsonSection.propTypes = { title: PropTypes.string.isRequired, value: PropTypes.any };
FaqHighlightsTab.propTypes = { tenants: PropTypes.array, selectedTenant: PropTypes.string, setSelectedTenant: PropTypes.func };

export default FaqHighlightsTab;
